// Pure data + layout helpers for the Ludograph screen. Scores are recombined
// live from the per-factor matrices that scripts/similarity.js writes.

export const FACTORS = [
  { key: "theme", label: "Theme", color: "var(--color-text)", dash: "7 3" },
  { key: "genre", label: "Genre", color: "var(--color-neutral-700)", dash: "3 3" },
  { key: "mechanics", label: "Mechanics", color: "var(--color-neutral-500)", dash: "1.5 3" },
  { key: "publisher", label: "Publisher", color: "var(--color-accent-2-400)", dash: "8 3 1.5 3" },
  { key: "difficulty", label: "Difficulty", color: "var(--color-accent-2-700)", dash: "4 2 1.5 2 1.5 2" },
  { key: "other", label: "Designer & feel", color: "var(--color-neutral-400)", dash: "1 6" },
];
export const SERIES = { key: "series", label: "Same series", color: "var(--color-accent)", dash: "none" };
export const ALL_FACTORS = [SERIES, ...FACTORS];
export const FACTOR_BY_KEY = Object.fromEntries(ALL_FACTORS.map((f) => [f.key, f]));

export const pretty = (tag) => {
  const s = tag.replace(/-/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Tag lists on each game that can be filtered on, with how to display their values.
export const TAG_KINDS = [
  { kind: "genres", label: "Genres", single: "Genre", format: pretty },
  { kind: "themes", label: "Themes", single: "Theme", format: pretty },
  { kind: "mechanics", label: "Mechanics", single: "Mechanic", format: (t) => t },
];

export function loadModel(similarity, components) {
  const games = similarity.games.map((g, i) => ({ ...g, index: i }));
  const n = games.length;
  const { seriesFloor, componentWeights } = similarity.meta;
  const pairs = [];
  const pairIndex = {};
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const c = {};
      FACTORS.forEach(({ key }) => (c[key] = components.components[key][i][j]));
      const p = { a: i, b: j, c, series: components.components.series[i][j] === 1 };
      pairs.push(p);
      pairIndex[i * n + j] = pairIndex[j * n + i] = p;
    }
  }
  const defaultWeights = Object.fromEntries(
    FACTORS.map(({ key }) => [key, Math.round(componentWeights[key] * 100)])
  );
  const defaultSeriesWeight = Math.round(seriesFloor * 100);
  return { games, n, pairs, pairIndex, defaultWeights, defaultSeriesWeight };
}

// Based on the formula in scripts/similarity.js, with user-chosen weights.
// `seriesWeight` (0–100) is the floor same-series pairs are lifted to; other
// pairs keep their base score, so 0 switches the series boost off.
// Returns the score plus how much each factor contributed to it.
export function scorePair(p, weights, seriesWeight) {
  const total = FACTORS.reduce((sum, { key }) => sum + weights[key], 0) || 1;
  const contrib = {};
  let base = 0;
  FACTORS.forEach(({ key }) => {
    contrib[key] = (weights[key] * p.c[key]) / total;
    base += contrib[key];
  });
  contrib.series = 0;
  const floor = seriesWeight / 100;
  if (!p.series || !floor) return { s: base, contrib };
  FACTORS.forEach(({ key }) => (contrib[key] *= 1 - floor));
  contrib.series = floor;
  return { s: floor + (1 - floor) * base, contrib };
}

const countTop = (members, pick) => {
  const counts = {};
  members.forEach((g) => pick(g).forEach((x) => (counts[x] = (counts[x] || 0) + 1)));
  return Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
};

export function buildGraph(model, weights, threshold, seriesWeight, maxLinks) {
  const { n, pairs, games } = model;
  const candidates = [];
  const perNode = Array.from({ length: n }, () => []);
  for (const p of pairs) {
    const { s, contrib } = scorePair(p, weights, seriesWeight);
    if (s >= threshold && s > 0) {
      const dom = ALL_FACTORS.reduce((best, f) => (contrib[f.key] > contrib[best] ? f.key : best), "theme");
      const e = { a: p.a, b: p.b, s, contrib, dom };
      candidates.push(e);
      perNode[p.a].push(e);
      perNode[p.b].push(e);
    }
  }
  const keep = new Set();
  perNode.forEach((list) => {
    list.sort((x, y) => y.s - x.s);
    list.slice(0, maxLinks).forEach((e) => keep.add(e));
  });
  const edges = candidates.filter((e) => keep.has(e));

  const adj = Array.from({ length: n }, () => []);
  const strength = new Array(n).fill(0);
  edges.forEach((e) => {
    adj[e.a].push({ o: e.b, e });
    adj[e.b].push({ o: e.a, e });
    strength[e.a] += e.s;
    strength[e.b] += e.s;
  });
  adj.forEach((list) => list.sort((x, y) => y.e.s - x.e.s));

  // Weighted label propagation for clusters.
  const label = [...Array(n).keys()];
  for (let it = 0; it < 25; it++) {
    let changed = 0;
    for (let i = 0; i < n; i++) {
      if (!adj[i].length) continue;
      const votes = {};
      adj[i].forEach(({ o, e }) => (votes[label[o]] = (votes[label[o]] || 0) + e.s));
      let best = label[i];
      let bestVote = votes[label[i]] || 0;
      for (const k in votes) {
        if (votes[k] > bestVote + 1e-9) {
          bestVote = votes[k];
          best = +k;
        }
      }
      if (best !== label[i]) {
        label[i] = best;
        changed++;
      }
    }
    if (!changed) break;
  }
  const groups = {};
  label.forEach((l, i) => {
    if (adj[i].length) (groups[l] = groups[l] || []).push(i);
  });
  const clusters = Object.values(groups)
    .filter((g) => g.length >= 3)
    .sort((a, b) => b.length - a.length)
    .slice(0, 9)
    .map((members, id) => {
      const gs = members.map((i) => games[i]);
      const byStrength = [...members].sort((a, b) => strength[b] - strength[a]);
      return {
        id,
        members,
        size: members.length,
        name: `${pretty(countTop(gs, (g) => g.genres))} · ${pretty(countTop(gs, (g) => g.themes))}`,
        preview: byStrength.slice(0, 3).map((i) => games[i].name).join(", "),
      };
    });

  const radius = strength.map((v) => 4 + Math.sqrt(v) * 4.4);
  const topList = [...Array(n).keys()].sort((a, b) => strength[b] - strength[a]);
  return { edges, adj, strength, clusters, radius, topList };
}

// Strongest chain of links (Dijkstra, cheaper edges are stronger links).
export function strongestPath(graph, n, a, b) {
  const dist = new Array(n).fill(Infinity);
  const prev = new Array(n).fill(-1);
  const done = new Array(n).fill(false);
  dist[a] = 0;
  for (;;) {
    let u = -1;
    let best = Infinity;
    for (let i = 0; i < n; i++) {
      if (!done[i] && dist[i] < best) {
        best = dist[i];
        u = i;
      }
    }
    if (u < 0 || u === b) break;
    done[u] = true;
    for (const { o, e } of graph.adj[u]) {
      const d = dist[u] + 1.1 - e.s;
      if (d < dist[o]) {
        dist[o] = d;
        prev[o] = u;
      }
    }
  }
  if (dist[b] === Infinity) return null;
  const path = [];
  for (let v = b; v !== -1; v = prev[v]) path.unshift(v);
  return path;
}

export function initialPositions(n) {
  return Array.from({ length: n }, (_, i) => {
    const angle = i * 2.399;
    const r = 14 * Math.sqrt(i + 1);
    return { x: Math.cos(angle) * r, y: Math.sin(angle) * r, vx: 0, vy: 0 };
  });
}

// One tick of the force simulation. Mutates sim.pos and sim.alpha.
export function step(sim, graph, dragIndex) {
  const P = sim.pos;
  const n = P.length;
  const { edges, radius } = graph;
  const a = sim.alpha;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const dx = P[j].x - P[i].x;
      const dy = P[j].y - P[i].y;
      const d2 = dx * dx + dy * dy || 0.01;
      const d = Math.sqrt(d2);
      let f = (1500 / d2) * a;
      const min = radius[i] + radius[j] + 6;
      if (d < min) f += (min - d) * 0.4;
      const fx = (dx / d) * f;
      const fy = (dy / d) * f;
      P[i].vx -= fx;
      P[i].vy -= fy;
      P[j].vx += fx;
      P[j].vy += fy;
    }
  }
  for (const e of edges) {
    const A = P[e.a];
    const B = P[e.b];
    const dx = B.x - A.x;
    const dy = B.y - A.y;
    const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
    const L = 24 + (1 - e.s) * 110 + radius[e.a] + radius[e.b];
    const f = (d - L) * 0.07 * e.s * a;
    const fx = (dx / d) * f;
    const fy = (dy / d) * f;
    A.vx += fx;
    A.vy += fy;
    B.vx -= fx;
    B.vy -= fy;
  }
  for (let i = 0; i < n; i++) {
    const p = P[i];
    if (i === dragIndex) {
      p.vx = p.vy = 0;
      continue;
    }
    p.vx -= p.x * 0.009 * a;
    p.vy -= p.y * 0.009 * a;
    p.vx *= 0.6;
    p.vy *= 0.6;
    p.x += p.vx;
    p.y += p.vy;
  }
  sim.alpha *= 0.985;
}

// View transform that fits every node (with room for labels) in the box.
export function fitView(sim, graph, drawerWidth) {
  const { w, h } = sim.size;
  const r = graph.radius;
  let x0 = 1e9;
  let x1 = -1e9;
  let y0 = 1e9;
  let y1 = -1e9;
  sim.pos.forEach((p, i) => {
    x0 = Math.min(x0, p.x - r[i]);
    x1 = Math.max(x1, p.x + r[i] + 70);
    y0 = Math.min(y0, p.y - r[i]);
    y1 = Math.max(y1, p.y + r[i]);
  });
  const pad = 40;
  const k = Math.max(
    0.2,
    Math.min((w - drawerWidth - pad * 2) / (x1 - x0), (h - pad * 2 - 40) / (y1 - y0), 2.4)
  );
  return { k, x: -(x0 + x1) / 2 - drawerWidth / 2 / k, y: -(y0 + y1) / 2 + 10 / k };
}
